import React, { useState, useEffect } from 'react';
import { Settings as SettingsIcon, User, Bell, Shield, CreditCard, LogOut, Save, Check, Loader2, AlertTriangle, Trash2, X, KeyRound, Mail, Zap, Calendar, ShieldCheck } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import Card from '../components/Card';
import { useAuth } from '../contexts/AuthContext';
import { usePreferences, CurrencyCode, ThemeMode, AccentColor, FontSize, ChartColorScheme, AnimationIntensity } from '../contexts/PreferencesContext';
import { useNavigate } from 'react-router-dom';
import api, { DeletionStatusResponse } from '../services/api';
import { PermissionGate } from '../components/PermissionGate';
import { BillingPricesPanel } from '../components/admin/BillingPricesPanel';

const Settings: React.FC = () => {
  const { user, isAuthenticated, logout, refreshUser } = useAuth();
  const { preferences, updateDisplayPreferences, updateNotificationPreferences } = usePreferences();
  const navigate = useNavigate();
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  // Profile edit state
  const [profileName, setProfileName] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Email change state
  const [newEmail, setNewEmail] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailCancelling, setEmailCancelling] = useState(false);
  const [emailMsg, setEmailMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Change password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [catIiCompliant, setCatIiCompliant] = useState(false);
  const [catIiCompliantSince, setCatIiCompliantSince] = useState('');
  const [catIiValidUntil, setCatIiValidUntil] = useState('');
  const [catIiInstitution, setCatIiInstitution] = useState('');
  const [catIiNumber, setCatIiNumber] = useState('');
  const [catIiSaving, setCatIiSaving] = useState(false);
  const [catIiMsg, setCatIiMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // AI quota (React Query)
  const { data: quota, isError: quotaError } = useQuery({
    queryKey: ['aiQuota'],
    queryFn: () => api.getAIQuota(),
    retry: 1,
    enabled: isAuthenticated,
  });

  // Account deletion state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletionStatus, setDeletionStatus] = useState<DeletionStatusResponse | null>(null);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Initialise profile name from user context
  useEffect(() => {
    if (user?.full_name) setProfileName(user.full_name);
  }, [user?.full_name]);

  useEffect(() => {
    setCatIiCompliant(user?.fais_cat_ii_compliant ?? false);
    setCatIiCompliantSince(user?.fais_cat_ii_compliant_since ?? '');
    setCatIiValidUntil(user?.fais_cat_ii_compliance_valid_until ?? '');
    setCatIiInstitution(user?.fais_cat_ii_certification_institution ?? '');
    setCatIiNumber(user?.fais_cat_ii_certification_number ?? '');
  }, [user]);

  // Local state synced with context
  const [notifications, setNotifications] = useState(preferences.notifications);
  const [display, setDisplay] = useState(preferences.display);

  // Fetch preferences from backend on mount
  useEffect(() => {
    const fetchPreferences = async () => {
      if (!isAuthenticated) return;
      setLoading(true);
      try {
        const serverPrefs = await api.getPreferences();
        // Map server preferences to local format
        const mappedNotifications = {
          priceAlerts: serverPrefs.notifications.price_alerts,
          portfolioUpdates: serverPrefs.notifications.portfolio_updates,
          marketNews: serverPrefs.notifications.market_news,
          emailDigest: serverPrefs.notifications.email_digest,
        };
        const mappedDisplay = {
          currency: serverPrefs.display.currency as CurrencyCode,
          theme: (serverPrefs.display.theme || 'dark') as ThemeMode,
          compactMode: serverPrefs.display.compact_mode ?? false,
          accentColor: (serverPrefs.display.accent_color || 'teal') as AccentColor,
          fontSize: (serverPrefs.display.font_size || 'medium') as FontSize,
          chartColorScheme: (serverPrefs.display.chart_color_scheme || 'classic') as ChartColorScheme,
          sidebarCollapsed: serverPrefs.display.sidebar_collapsed ?? false,
          animationIntensity: (serverPrefs.display.animation_intensity || 'full') as AnimationIntensity,
        };
        setNotifications(mappedNotifications);
        setDisplay(mappedDisplay);
        // Also update context
        updateDisplayPreferences(mappedDisplay);
        updateNotificationPreferences(mappedNotifications);
      } catch {
        // If backend fails, use local preferences
        console.log('Using local preferences');
      } finally {
        setLoading(false);
      }
    };
    fetchPreferences();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Fetch deletion status on mount
  useEffect(() => {
    const fetchDeletionStatus = async () => {
      if (!isAuthenticated) return;
      try {
        const status = await api.getDeletionStatus();
        setDeletionStatus(status);
      } catch {
        // Ignore errors, user may not have requested deletion
      }
    };
    fetchDeletionStatus();
  }, [isAuthenticated]);

  const handleSaveName = async () => {
    setProfileSaving(true);
    setProfileMsg(null);
    try {
      await api.updateProfile({ name: profileName });
      await refreshUser();
      setProfileMsg({ type: 'success', text: 'Name updated successfully' });
    } catch (err) {
      setProfileMsg({ type: 'error', text: err instanceof Error ? err.message : 'Failed to update name' });
    } finally {
      setProfileSaving(false);
    }
  };

  const handleSaveEmail = async () => {
    setEmailSaving(true);
    setEmailMsg(null);
    try {
      await api.updateProfile({ email: newEmail });
      setEmailMsg({ type: 'success', text: `Verification email sent to ${newEmail}` });
      setNewEmail('');
      await refreshUser();
    } catch (err) {
      setEmailMsg({ type: 'error', text: err instanceof Error ? err.message : 'Failed to request email change' });
    } finally {
      setEmailSaving(false);
    }
  };

  const handleCancelEmailChange = async () => {
    setEmailCancelling(true);
    setEmailMsg(null);
    try {
      await api.cancelEmailChange();
      await refreshUser();
      setEmailMsg({ type: 'success', text: 'Email change cancelled' });
    } catch (err) {
      setEmailMsg({ type: 'error', text: err instanceof Error ? err.message : 'Failed to cancel email change' });
    } finally {
      setEmailCancelling(false);
    }
  };

  const handleChangePassword = async () => {
    setPasswordMsg(null);
    if (newPassword !== confirmPassword) {
      setPasswordMsg({ type: 'error', text: 'New passwords do not match' });
      return;
    }
    setPasswordSaving(true);
    try {
      await api.updateProfile({ current_password: currentPassword, new_password: newPassword });
      setPasswordMsg({ type: 'success', text: 'Password changed successfully' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPasswordMsg({ type: 'error', text: err instanceof Error ? err.message : 'Failed to change password' });
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleSaveCatIICompliance = async () => {
    setCatIiSaving(true);
    setCatIiMsg(null);
    try {
      await api.updateProfile({
        fais_cat_ii_compliant: catIiCompliant,
        fais_cat_ii_compliant_since: catIiCompliantSince || null,
        fais_cat_ii_compliance_valid_until: catIiValidUntil || null,
        fais_cat_ii_certification_institution: catIiInstitution || null,
        fais_cat_ii_certification_number: catIiNumber || null,
      });
      await refreshUser();
      setCatIiMsg({ type: 'success', text: 'CAT II compliance details saved' });
    } catch (err) {
      setCatIiMsg({ type: 'error', text: err instanceof Error ? err.message : 'Failed to save CAT II details' });
    } finally {
      setCatIiSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save to backend
      await api.updatePreferences({
        notifications: {
          price_alerts: notifications.priceAlerts,
          portfolio_updates: notifications.portfolioUpdates,
          market_news: notifications.marketNews,
          email_digest: notifications.emailDigest,
          email_on_alert_trigger: true,
        },
        display: {
          currency: display.currency,
          theme: display.theme,
          compact_mode: display.compactMode,
          accent_color: display.accentColor,
          font_size: display.fontSize,
          chart_color_scheme: display.chartColorScheme,
          sidebar_collapsed: display.sidebarCollapsed,
          animation_intensity: display.animationIntensity,
        },
      });
    } catch {
      console.log('Backend save failed, saving locally only');
    }
    // Update context (which auto-saves to localStorage)
    updateDisplayPreferences(display);
    updateNotificationPreferences(notifications);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleRequestDeletion = async () => {
    if (!deletePassword) {
      setDeleteError('Password is required');
      return;
    }

    setDeleteLoading(true);
    setDeleteError('');

    try {
      await api.requestDeletion(deletePassword, deleteReason || undefined);
      const status = await api.getDeletionStatus();
      setDeletionStatus(status);
      setShowDeleteModal(false);
      setDeletePassword('');
      setDeleteReason('');
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to request deletion');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancelDeletion = async () => {
    if (!deletePassword) {
      setDeleteError('Password is required');
      return;
    }

    setDeleteLoading(true);
    setDeleteError('');

    try {
      await api.cancelDeletion(deletePassword);
      setDeletionStatus({ deletion_requested: false });
      setShowDeleteModal(false);
      setDeletePassword('');
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to cancel deletion');
    } finally {
      setDeleteLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <SettingsIcon size={48} className="mx-auto text-slate-600 mb-4" />
          <h2 className="text-xl font-semibold mb-2">Sign in to access settings</h2>
          <p className="text-slate-400 mb-4">You need to be logged in to manage your settings</p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-2 bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold rounded-lg transition-colors"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 space-y-6 pb-20 lg:pb-0">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <SettingsIcon size={28} className="text-teal-400" />
          <h1 className="text-2xl font-bold">Settings</h1>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className={`flex items-center gap-2 px-4 py-2 min-h-[44px] rounded-lg font-semibold transition-colors ${
            saved
              ? 'bg-emerald-500 text-white'
              : saving
              ? 'bg-slate-600 text-slate-300 cursor-not-allowed'
              : 'bg-teal-500 hover:bg-teal-400 text-slate-900'
          }`}
        >
          {saving ? <Loader2 size={18} className="animate-spin" /> : saved ? <Check size={18} /> : <Save size={18} />}
          {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Changes'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profile Section */}
        <Card
          title={
            <div className="flex items-center gap-2">
              <User size={18} className="text-blue-400" />
              <span>Profile</span>
            </div>
          }
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Full Name</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={profileName}
                  onChange={(e) => setProfileName(e.target.value)}
                  className="flex-1 bg-slate-700/50 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-teal-500"
                />
                <button
                  onClick={handleSaveName}
                  disabled={profileSaving}
                  className="px-4 py-2 bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1"
                >
                  {profileSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  Save
                </button>
              </div>
              {profileMsg && (
                <p className={`text-xs mt-1 ${profileMsg.type === 'success' ? 'text-emerald-400' : 'text-red-400'}`}>
                  {profileMsg.text}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Email Address</label>
              <input
                type="email"
                value={user?.email || ''}
                disabled
                className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-4 py-2 text-slate-300 mb-2"
              />
              {user?.email_change_pending && (
                <div className="flex items-center justify-between gap-2 text-xs text-amber-400 mb-2">
                  <div className="flex items-center gap-2">
                    <Mail size={12} />
                    <span>Verification pending for <strong>{user.email_change_pending}</strong> — check your inbox</span>
                  </div>
                  <button
                    onClick={handleCancelEmailChange}
                    disabled={emailCancelling}
                    className="flex items-center gap-1 text-xs text-slate-400 hover:text-red-400 transition-colors disabled:opacity-50"
                  >
                    {emailCancelling ? <Loader2 size={10} className="animate-spin" /> : <X size={10} />}
                    Cancel
                  </button>
                </div>
              )}
              <div className="flex gap-2 mt-1">
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="New email address"
                  className="flex-1 bg-slate-700/50 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-teal-500"
                />
                <button
                  onClick={handleSaveEmail}
                  disabled={emailSaving || !newEmail || newEmail === user?.email}
                  className="px-4 py-2 bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1"
                >
                  {emailSaving ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                  Change
                </button>
              </div>
              {emailMsg && (
                <p className={`text-xs mt-1 ${emailMsg.type === 'success' ? 'text-emerald-400' : 'text-red-400'}`}>
                  {emailMsg.text}
                </p>
              )}
            </div>
          </div>
        </Card>

        {/* CAT II Compliance Section */}
        <Card
          title={
            <div className="flex items-center gap-2">
              <ShieldCheck size={18} className="text-emerald-400" />
              <span>FAIS CAT II Compliance</span>
            </div>
          }
        >
          <div className="space-y-4">
            <p className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/25 rounded-lg px-3 py-2">
              Compliance is currently self-declared and not externally verified in this release.
            </p>

            <label className="flex items-center gap-2 cursor-pointer min-h-[44px]">
              <input
                type="checkbox"
                checked={catIiCompliant}
                onChange={(e) => setCatIiCompliant(e.target.checked)}
                className="sr-only peer"
              />
              <div className="relative">
                <div className="w-11 h-6 bg-slate-600 rounded-full peer-checked:bg-teal-500 transition-colors" />
                <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full peer-checked:translate-x-5 transition-transform" />
              </div>
              <span className="text-sm text-slate-200">I am a self-affirmed FAIS CAT II compliant advisor</span>
            </label>

            <label className="block text-sm text-slate-400">Compliant Since</label>
            <div className="flex items-center gap-2">
              <Calendar size={16} className="text-slate-400" />
              <input
                type="date"
                value={catIiCompliantSince}
                onChange={(e) => setCatIiCompliantSince(e.target.value)}
                className="flex-1 bg-slate-700/50 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-teal-500"
              />
            </div>

            <label className="block text-sm text-slate-400">Current Certification Valid Until</label>
            <div className="flex items-center gap-2">
              <Calendar size={16} className="text-slate-400" />
              <input
                type="date"
                value={catIiValidUntil}
                onChange={(e) => setCatIiValidUntil(e.target.value)}
                className="flex-1 bg-slate-700/50 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-teal-500"
              />
            </div>

            <label className="block text-sm text-slate-400">Certifying Institution</label>
            <input
              type="text"
              value={catIiInstitution}
              onChange={(e) => setCatIiInstitution(e.target.value)}
              className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-teal-500"
              placeholder="e.g. FSB (FCA)"
            />

            <label className="block text-sm text-slate-400">Certification Number</label>
            <input
              type="text"
              value={catIiNumber}
              onChange={(e) => setCatIiNumber(e.target.value)}
              className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-teal-500"
              placeholder="e.g. CAT II 0000-0000"
            />

            <button
              onClick={handleSaveCatIICompliance}
              disabled={catIiSaving}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold rounded-lg transition-colors disabled:opacity-50"
            >
              {catIiSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Save Compliance Details
            </button>

            {catIiMsg && (
              <p className={`text-xs ${catIiMsg.type === 'success' ? 'text-emerald-400' : 'text-red-400'}`}>
                {catIiMsg.text}
              </p>
            )}
          </div>
        </Card>

        {/* Change Password Section */}
        <Card
          title={
            <div className="flex items-center gap-2">
              <KeyRound size={18} className="text-amber-400" />
              <span>Change Password</span>
            </div>
          }
        >
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Current Password</label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-teal-500"
                placeholder="Enter current password"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-teal-500"
                placeholder="At least 8 characters"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Confirm New Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-teal-500"
                placeholder="Repeat new password"
              />
            </div>
            {passwordMsg && (
              <p className={`text-xs ${passwordMsg.type === 'success' ? 'text-emerald-400' : 'text-red-400'}`}>
                {passwordMsg.text}
              </p>
            )}
            <button
              onClick={handleChangePassword}
              disabled={passwordSaving || !currentPassword || !newPassword || !confirmPassword}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold rounded-lg transition-colors disabled:opacity-50"
            >
              {passwordSaving ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />}
              Change Password
            </button>
          </div>
        </Card>

        {/* Notifications Section */}
        <Card
          title={
            <div className="flex items-center gap-2">
              <Bell size={18} className="text-amber-400" />
              <span>Notifications</span>
            </div>
          }
        >
          <div className="space-y-4">
            {[
              { key: 'priceAlerts', label: 'Price Alerts', desc: 'Get notified when your price alerts trigger' },
              { key: 'portfolioUpdates', label: 'Portfolio Updates', desc: 'Daily portfolio performance summary' },
              { key: 'marketNews', label: 'Market News', desc: 'Breaking news about your watchlist' },
              { key: 'emailDigest', label: 'Email Digest', desc: 'Weekly email summary of your investments' },
            ].map((item) => (
              <label key={item.key} className="flex items-center justify-between cursor-pointer group min-h-[44px]">
                <div>
                  <p className="text-sm font-medium">{item.label}</p>
                  <p className="text-xs text-slate-500">{item.desc}</p>
                </div>
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={notifications[item.key as keyof typeof notifications]}
                    onChange={(e) =>
                      setNotifications((prev) => ({ ...prev, [item.key]: e.target.checked }))
                    }
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-600 rounded-full peer peer-checked:bg-teal-500 transition-colors"></div>
                  <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full peer-checked:translate-x-5 transition-transform"></div>
                </div>
              </label>
            ))}
          </div>
        </Card>

        {/* Display Section */}
        <Card
          title={
            <div className="flex items-center gap-2">
              <SettingsIcon size={18} className="text-purple-400" />
              <span>Display</span>
            </div>
          }
        >
          <div className="space-y-5">
            {/* Currency */}
            <div>
              <label className="block text-sm text-slate-400 mb-1">Currency</label>
              <select
                value={display.currency}
                onChange={(e) => {
                  setDisplay((prev) => ({ ...prev, currency: e.target.value }));
                  updateDisplayPreferences({ currency: e.target.value as CurrencyCode });
                }}
                className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-teal-500"
              >
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (€)</option>
                <option value="GBP">GBP (£)</option>
                <option value="ZAR">ZAR (R)</option>
              </select>
            </div>

            {/* Theme picker */}
            <div>
              <label className="block text-sm text-slate-400 mb-2">Theme</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {([
                  { value: 'dark',           label: 'Dark',       bg: '#0f172a', sidebar: '#1e293b', accent: '#2dd4bf', textColor: '#ffffff' },
                  { value: 'shadcn-zinc',    label: 'Zinc',       bg: '#09090b', sidebar: '#18181b', accent: '#a1a1aa', textColor: '#ffffff' },
                  { value: 'midnight-blue',  label: 'Midnight',   bg: '#060d1a', sidebar: '#0a1628', accent: '#fbbf24', textColor: '#ffffff' },
                  { value: 'terminal-green', label: 'Terminal',   bg: '#000000', sidebar: '#030303', accent: '#00ff41', textColor: '#00cc33' },
                  { value: 'warm-dark',      label: 'Warm',       bg: '#12100e', sidebar: '#1c1814', accent: '#f59e0b', textColor: '#ffffff' },
                  { value: 'snow',           label: 'Snow',       bg: '#f0f4f8', sidebar: '#ffffff', accent: '#2dd4bf', textColor: '#0f172a' },
                  { value: 'dracula',        label: 'Dracula',    bg: '#282a36', sidebar: '#21222c', accent: '#bd93f9', textColor: '#f8f8f2' },
                  { value: 'nord',           label: 'Nord',       bg: '#2e3440', sidebar: '#3b4252', accent: '#88c0d0', textColor: '#eceff4' },
                  { value: 'ocean',          label: 'Ocean',      bg: '#0a192f', sidebar: '#112240', accent: '#64ffda', textColor: '#ccd6f6' },
                  { value: 'solarized-dark', label: 'Solarized',  bg: '#002b36', sidebar: '#073642', accent: '#268bd2', textColor: '#839496' },
                  { value: 'rose',           label: 'Rose',       bg: '#fff1f2', sidebar: '#fce7f3', accent: '#f43f5e', textColor: '#0f172a' },
                ] as const).map((t) => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => {
                      setDisplay((prev) => ({ ...prev, theme: t.value }));
                      updateDisplayPreferences({ theme: t.value });
                    }}
                    className={`flex flex-col items-center gap-1.5 p-2 rounded-lg border-2 transition-all ${
                      display.theme === t.value
                        ? 'border-teal-400 bg-teal-500/10'
                        : 'border-slate-600 hover:border-slate-500'
                    }`}
                  >
                    {/* Mini preview */}
                    <div
                      className="w-full h-10 rounded overflow-hidden flex"
                      style={{ backgroundColor: t.bg }}
                    >
                      <div className="w-3 h-full" style={{ backgroundColor: t.sidebar }} />
                      <div className="flex-1 flex flex-col justify-end p-0.5 gap-0.5">
                        <div className="h-1 rounded-full opacity-60" style={{ backgroundColor: t.accent, width: '60%' }} />
                        <div className="h-1 rounded-full opacity-30" style={{ backgroundColor: t.textColor, width: '80%' }} />
                      </div>
                    </div>
                    <span className="text-xs text-slate-300">{t.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Accent color */}
            <div>
              <label className="block text-sm text-slate-400 mb-2">Accent Color</label>
              <div className="flex gap-2">
                {([
                  { value: 'teal',   color: '#2dd4bf', label: 'Teal'   },
                  { value: 'blue',   color: '#60a5fa', label: 'Blue'   },
                  { value: 'gold',   color: '#fbbf24', label: 'Gold'   },
                  { value: 'green',  color: '#4ade80', label: 'Green'  },
                  { value: 'violet', color: '#a78bfa', label: 'Violet' },
                ] as const).map((a) => (
                  <button
                    key={a.value}
                    type="button"
                    title={a.label}
                    onClick={() => {
                      setDisplay((prev) => ({ ...prev, accentColor: a.value }));
                      updateDisplayPreferences({ accentColor: a.value });
                    }}
                    className={`w-8 h-8 rounded-full border-2 transition-all ${
                      display.accentColor === a.value ? 'border-white scale-110' : 'border-transparent hover:scale-105'
                    }`}
                    style={{ backgroundColor: a.color }}
                  />
                ))}
              </div>
            </div>

            {/* Font size */}
            <div>
              <label className="block text-sm text-slate-400 mb-2">Font Size</label>
              <div className="flex gap-2">
                {(['small', 'medium', 'large'] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => {
                      setDisplay((prev) => ({ ...prev, fontSize: s }));
                      updateDisplayPreferences({ fontSize: s });
                    }}
                    className={`flex-1 py-1.5 rounded-lg border text-sm capitalize transition-all ${
                      display.fontSize === s
                        ? 'border-teal-400 bg-teal-500/10 text-teal-300'
                        : 'border-slate-600 text-slate-400 hover:border-slate-500'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* Chart color scheme */}
            <div>
              <label className="block text-sm text-slate-400 mb-2">Chart Colors</label>
              <div className="flex gap-2">
                {([
                  { value: 'classic',        label: 'Classic',      dots: ['#2dd4bf','#60a5fa','#f59e0b'] },
                  { value: 'high-contrast',  label: 'High Contrast',dots: ['#ffffff','#ffff00','#00ffff'] },
                  { value: 'colorblind',     label: 'Colorblind',   dots: ['#0077bb','#009988','#ee7733'] },
                ] as const).map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => {
                      setDisplay((prev) => ({ ...prev, chartColorScheme: c.value }));
                      updateDisplayPreferences({ chartColorScheme: c.value });
                    }}
                    className={`flex-1 flex flex-col items-center gap-1 py-2 rounded-lg border text-xs transition-all ${
                      display.chartColorScheme === c.value
                        ? 'border-teal-400 bg-teal-500/10 text-teal-300'
                        : 'border-slate-600 text-slate-400 hover:border-slate-500'
                    }`}
                  >
                    <div className="flex gap-1">
                      {c.dots.map((d, i) => (
                        <span key={i} className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: d }} />
                      ))}
                    </div>
                    <span>{c.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Animation intensity */}
            <div>
              <label className="block text-sm text-slate-400 mb-2">Animations</label>
              <div className="flex gap-2">
                {([
                  { value: 'full',    label: 'Full',    desc: 'All transitions' },
                  { value: 'reduced', label: 'Reduced', desc: 'Subtle only'     },
                  { value: 'none',    label: 'None',    desc: 'No motion'       },
                ] as const).map((a) => (
                  <button
                    key={a.value}
                    type="button"
                    onClick={() => {
                      setDisplay((prev) => ({ ...prev, animationIntensity: a.value }));
                      updateDisplayPreferences({ animationIntensity: a.value });
                    }}
                    className={`flex-1 flex flex-col items-center py-2 rounded-lg border text-xs transition-all ${
                      display.animationIntensity === a.value
                        ? 'border-teal-400 bg-teal-500/10 text-teal-300'
                        : 'border-slate-600 text-slate-400 hover:border-slate-500'
                    }`}
                  >
                    <span className="font-medium">{a.label}</span>
                    <span className="text-slate-500 text-[10px]">{a.desc}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Compact mode toggle */}
            <label className="flex items-center justify-between cursor-pointer min-h-[44px]">
              <div>
                <p className="text-sm font-medium">Compact Mode</p>
                <p className="text-xs text-slate-500">Show more data with smaller spacing</p>
              </div>
              <div className="relative">
                <input
                  type="checkbox"
                  checked={display.compactMode}
                  onChange={(e) => {
                    setDisplay((prev) => ({ ...prev, compactMode: e.target.checked }));
                    updateDisplayPreferences({ compactMode: e.target.checked });
                  }}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-600 rounded-full peer peer-checked:bg-teal-500 transition-colors"></div>
                <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full peer-checked:translate-x-5 transition-transform"></div>
              </div>
            </label>

            {/* Price ticker toggle */}
            <label className="flex items-center justify-between cursor-pointer min-h-[44px]">
              <div>
                <p className="text-sm font-medium">Price Ticker</p>
                <p className="text-xs text-slate-500">Show live price ticker bar at the top</p>
              </div>
              <div className="relative">
                <input
                  type="checkbox"
                  checked={display.showPriceTicker}
                  onChange={(e) => {
                    setDisplay((prev) => ({ ...prev, showPriceTicker: e.target.checked }));
                    updateDisplayPreferences({ showPriceTicker: e.target.checked });
                  }}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-600 rounded-full peer peer-checked:bg-teal-500 transition-colors"></div>
                <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full peer-checked:translate-x-5 transition-transform"></div>
              </div>
            </label>
          </div>
        </Card>

        {/* AI Usage Section */}
        <Card
          title={
            <div className="flex items-center gap-2">
              <Zap size={18} className="text-teal-400" />
              <span>AI Usage</span>
            </div>
          }
        >
          {quotaError ? (
            <p className="text-slate-500 text-sm">Quota data unavailable</p>
          ) : !quota ? (
            <div className="space-y-2">
              <div className="animate-pulse h-4 bg-slate-700 rounded w-24" />
              <div className="animate-pulse h-3 bg-slate-700 rounded w-40" />
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-400">Plan</span>
                <span className="text-sm font-medium capitalize">{quota.tier}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-400">Queries used</span>
                <span className="text-sm font-medium">
                  {quota.quota_limit === -1
                    ? 'Rate-limited'
                    : `${quota.current_usage} / ${quota.quota_limit}`}
                </span>
              </div>
              {quota.quota_limit !== -1 && (
                <div>
                  <div className="w-full bg-slate-700 rounded-full h-1.5">
                    <div
                      className="bg-teal-500 h-1.5 rounded-full transition-all"
                      style={{ width: `${Math.min(quota.percentage_used, 100)}%` }}
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{quota.percentage_used.toFixed(0)}% used</p>
                </div>
              )}
              {quota.reset_date && (
                <p className="text-xs text-slate-500">
                  Resets {new Date(quota.reset_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </p>
              )}
            </div>
          )}
        </Card>

        {/* Account Section */}
        <Card
          title={
            <div className="flex items-center gap-2">
              <Shield size={18} className="text-emerald-400" />
              <span>Account</span>
            </div>
          }
        >
          <div className="space-y-4">
            <button
              onClick={() => navigate('/subscription')}
              className="w-full flex items-center justify-between px-4 py-3 bg-slate-700/50 hover:bg-slate-700 rounded-lg transition-colors"
            >
              <div className="flex items-center gap-3">
                <CreditCard size={18} className="text-teal-400" />
                <span>Manage Subscription</span>
              </div>
              <span className="text-slate-400">→</span>
            </button>

            <hr className="border-slate-700" />

            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors"
            >
              <LogOut size={18} />
              <span>Sign Out</span>
            </button>
          </div>
        </Card>
      </div>

      {/* Danger Zone - Full width */}
      <Card
        title={
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-red-400" />
            <span className="text-red-400">Danger Zone</span>
          </div>
        }
      >
        <div className="space-y-4">
          {deletionStatus?.deletion_requested ? (
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle size={20} className="text-red-400 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-medium text-red-400">Account Deletion Scheduled</h4>
                  <p className="text-sm text-slate-400 mt-1">
                    Your account is scheduled for deletion on{' '}
                    <span className="text-white font-medium">
                      {deletionStatus.deletion_scheduled_at && formatDate(deletionStatus.deletion_scheduled_at)}
                    </span>
                    {deletionStatus.days_remaining !== undefined && (
                      <> ({deletionStatus.days_remaining} days remaining)</>
                    )}
                  </p>
                  {deletionStatus.deletion_reason && (
                    <p className="text-xs text-slate-500 mt-2">
                      Reason: {deletionStatus.deletion_reason}
                    </p>
                  )}
                  <button
                    onClick={() => setShowDeleteModal(true)}
                    className="mt-3 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm transition-colors"
                  >
                    Cancel Deletion Request
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium">Delete Account</h4>
                <p className="text-sm text-slate-400">
                  Permanently delete your account and all associated data. This action has a 30-day grace period.
                </p>
              </div>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors"
              >
                <Trash2 size={16} />
                <span>Delete Account</span>
              </button>
            </div>
          )}
        </div>
      </Card>

      {/* Admin: Billing Prices */}
      <PermissionGate permission="admin:config-write">
        <BillingPricesPanel />
      </PermissionGate>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => {
              setShowDeleteModal(false);
              setDeletePassword('');
              setDeleteReason('');
              setDeleteError('');
            }}
          />
          <div className="relative bg-slate-800 rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
            <button
              onClick={() => {
                setShowDeleteModal(false);
                setDeletePassword('');
                setDeleteReason('');
                setDeleteError('');
              }}
              className="absolute top-4 right-4 text-slate-400 hover:text-white"
            >
              <X size={20} />
            </button>

            {deletionStatus?.deletion_requested ? (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-slate-700 rounded-lg">
                    <AlertTriangle size={24} className="text-amber-400" />
                  </div>
                  <h3 className="text-lg font-semibold">Cancel Account Deletion</h3>
                </div>
                <p className="text-slate-400 mb-4">
                  Enter your password to cancel the account deletion request. Your account will be fully restored.
                </p>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-red-500/20 rounded-lg">
                    <Trash2 size={24} className="text-red-400" />
                  </div>
                  <h3 className="text-lg font-semibold">Delete Account</h3>
                </div>
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg mb-4">
                  <p className="text-sm text-red-300">
                    <strong>Warning:</strong> This will schedule your account for permanent deletion. You have 30 days to cancel before your data is permanently removed.
                  </p>
                </div>
                <p className="text-slate-400 mb-4">
                  All your watchlists, portfolios, alerts, and preferences will be deleted after the grace period.
                </p>
              </>
            )}

            {deleteError && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg mb-4">
                <p className="text-sm text-red-400">{deleteError}</p>
              </div>
            )}

            <div className="space-y-4">
              {!deletionStatus?.deletion_requested && (
                <div>
                  <label className="block text-sm text-slate-400 mb-1">
                    Reason for leaving (optional)
                  </label>
                  <textarea
                    value={deleteReason}
                    onChange={(e) => setDeleteReason(e.target.value)}
                    placeholder="Help us improve by telling us why you're leaving..."
                    className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-red-500 resize-none"
                    rows={3}
                  />
                </div>
              )}

              <div>
                <label className="block text-sm text-slate-400 mb-1">
                  Confirm with your password
                </label>
                <input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-red-500"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeletePassword('');
                    setDeleteReason('');
                    setDeleteError('');
                  }}
                  className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
                {deletionStatus?.deletion_requested ? (
                  <button
                    onClick={handleCancelDeletion}
                    disabled={deleteLoading}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold rounded-lg transition-colors disabled:opacity-50"
                  >
                    {deleteLoading ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <>
                        <Check size={18} />
                        Keep Account
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    onClick={handleRequestDeletion}
                    disabled={deleteLoading}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-400 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
                  >
                    {deleteLoading ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <>
                        <Trash2 size={18} />
                        Delete Account
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
